import * as React from "react";
import cs from "classnames";
import i18n from "i18next";
import { Marker } from "../../api/EvalWorkflowApi";
import { ParamType } from "../../api/GrammarApi";
import ParamTypeSelectors from "../param-types/ParamTypeSelectors";
import "./marker-menu.scss";

type MarkerMenuProps = {
  marker: Marker;
  obsTypes?: string[];
  paramTypesByObsTypes?: Record<string, ParamType[]>;
  onDelete: (marker: Marker) => void;
  onMouseLeave?: () => void;
  onMarkerChange?: (marker: Marker) => void;
};

export default function MarkerMenu({
  marker,
  onDelete,
  onMouseLeave,
  paramTypesByObsTypes = {},
  obsTypes,
  onMarkerChange,
}: MarkerMenuProps) {
  const obsType = marker.obsType || obsTypes[0];
  const paramTypes = paramTypesByObsTypes[obsType] || [];
  const className = cs({ "marker-menu-bloc": obsTypes.length > 0 || paramTypes.length > 0 });

  const handleSelect = React.useCallback(
    (paramCode: string, paramValue: string) => {
      onMarkerChange?.({
        ...marker,
        valueByParam: {
          ...(marker?.valueByParam || {}),
          [paramCode]: paramValue,
        },
      });
    },
    [marker, onMarkerChange]
  );

  const handleObsTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const obsType = e.target.value;
    const newParamTypes = paramTypesByObsTypes[obsType] || [];
    onMarkerChange?.({
      ...marker,
      obsType,
      valueByParam: newParamTypes
        .filter((param) => param.defaultOpt)
        .reduce((acc, param) => ({ ...acc, [param.code]: param.defaultOpt }), {}),
    });
  };

  return (
    <div onMouseLeave={onMouseLeave} className={className}>
      <button type="button" className="btn btn-light text-left" onClick={() => onDelete(marker)}>
        <i className="fa fa-trash" /> {i18n.t("global.delete")}
      </button>
      {obsTypes.length > 1 && (
        <div>
          <label className="sr-only" htmlFor="obsType">
            Obs Type
          </label>
          <select id="obsType" value={marker.obsType} onChange={handleObsTypeChange}>
            {obsTypes.map((obsType) => (
              <option key={obsType} value={obsType}>
                {obsType}
              </option>
            ))}
          </select>
        </div>
      )}
      <ParamTypeSelectors
        paramTypes={paramTypes}
        onSelect={handleSelect}
        getValue={(paramCode) => marker.valueByParam?.[paramCode]}
      />
    </div>
  );
}

type AddMarkerMenuProps = {
  initialMarker: Marker;
  paramTypesByObsTypes?: Record<string, ParamType[]>;
  obsTypes: string[];
  onMouseLeave?: () => void;
  onValidate?: (savedMarker: Marker) => void;
};

export function AddMarkerMenu({
  initialMarker,
  paramTypesByObsTypes,
  obsTypes,
  onMouseLeave,
  onValidate,
}: AddMarkerMenuProps) {
  const [marker, setMarker] = React.useState<Marker>(() => ({
    ...initialMarker,
    obsType: initialMarker.obsType || obsTypes[0],
  }));

  const obsType = marker.obsType;

  const paramTypes = paramTypesByObsTypes[obsType] || [];

  const className = cs({ "marker-menu-bloc": obsTypes.length > 0 || paramTypes.length > 0 });

  const isValid =
    marker.obsType &&
    paramTypes.filter((paramType) => paramType.required).every((paramType) => !!marker.valueByParam?.[paramType.code]);

  const handleSelect = React.useCallback((paramCode: string, paramValue: string) => {
    setMarker((prevMarker) => ({
      ...prevMarker,
      valueByParam: {
        ...(prevMarker?.valueByParam || {}),
        [paramCode]: paramValue,
      },
    }));
  }, []);

  const handleObsTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const obsType = e.target.value;
    const newParamTypes = paramTypesByObsTypes[obsType] || [];
    setMarker((prevMarker) => ({
      ...prevMarker,
      obsType,
      valueByParam:
        newParamTypes
          .filter((param) => param.defaultOpt)
          .reduce((acc, param) => ({ ...acc, [param.code]: param.defaultOpt }), {}) || {},
    }));
  };

  return (
    <div className={className}>
      {obsTypes.length > 1 && (
        <>
          <label className="sr-only" htmlFor="obsType">
            Obs Type
          </label>
          <select id="obsType" value={marker.obsType} onChange={handleObsTypeChange}>
            {obsTypes.map((obsType) => (
              <option key={obsType} value={obsType}>
                {obsType}
              </option>
            ))}
          </select>
        </>
      )}
      <ParamTypeSelectors
        paramTypes={paramTypes}
        onSelect={handleSelect}
        getValue={(paramCode) => marker.valueByParam?.[paramCode]}
      />
      <div className="marker-menu-bloc-buttons">
        <button type="button" className="btn btn-light text-left" onClick={onMouseLeave}>
          <i className="fa fa-times" /> {i18n.t("global.cancel")}
        </button>
        <button
          type="button"
          className="btn btn-light text-left"
          onClick={() => onValidate(marker)}
          disabled={!isValid}
        >
          <i className="fa fa-check" /> {i18n.t("global.validate")}
        </button>
      </div>
    </div>
  );
}
